import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthUser } from '../../contexts/AuthProvider/hooks';
import { useConfig } from '../../eliot-components/src/ConfigProvider/hooks';
import Layout from '../../Layout/Layout';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import EditTargetSystemDialog from './components/EditTargetSystemDialog/EditTargetSystemDialog';
import { MedisanteTargetSystem, TargetSystemsSortBy } from './types';
import { Role, hasOneOfOrgRoles } from '../../contexts/AuthProvider/types';
import Visible from '../../components/Visible/Visible';
import TriggerTestDialog from './components/TriggerTestDialog/TriggerTestDialog';
import { MediaUp, MediaDown } from '../../components/MediaQueries/MediaQueries';
import MobileLayout from './components/MobileLayout/MobileLayout';
import { useManageContext } from '../../contexts/ManageContext/ManageContext';
import DesktopLayout from './components/DesktopLayout/DesktopLayout';
import { partialRight } from '../../utils/helpers';
import {
  sortTargetSystems,
  targetSystemsSortingReducer,
  mobileSortOptions,
  deviceForTargetSystemInfo,
  notFullySuportedModel,
} from './config';
import {
  useSortState,
  SortState,
  SortOptionSelectItem,
} from '../../utils/sort';
import DeviceHub from '@material-ui/icons/DeviceHub';
import { categories } from '../Dashboard/constants';
import { chartColor } from '../Dashboard/provider';
import ConfirmationDialog from '../../eliot-components/src/ConfirmationDialog/ConfirmationDialog';
import { useLocation } from 'react-router';
import {
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import { Cached } from '@material-ui/icons';
import MedSelect from '../../eliot-components/src/Common/MedSelect';
import { filtersFromQueryString } from '../Devices/components/Filters/queryString';
import wrapFilters from '../Devices/components/Filters/wrapFilters';
import Filters from './components/Filters/Filters';
import {
  useEditTargetSystemMutation,
  useGetDevicesQuery,
  useGetTargetSystemsQuery,
  useRemoveTsDevicesMutation,
  useRemoveTsMutation,
} from '../../store/api-slice';
import { useSnackbar } from 'notistack';
import ConfirmationAction from '../../eliot-components/src/ConfirmationDialog/ConfirmationDialog';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme: Theme) => ({
  placeholder: {
    textAlign: 'center',
    marginTop: theme.spacing(5),
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 2,
  },
  page: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      '& .mainContent': {
        padding: theme.spacing(1),
      },
    },
  },
  refresh: {
    position: 'relative',
    textAlign: 'right',
    paddingBottom: theme.spacing(3),
  },
  refreshProgress: {
    padding: theme.spacing(1),
  },
  refreshIcon: {
    display: 'inline-block',
  },
  refreshProgressWrapper: {
    display: 'inline-block',
    width: '40px',
    height: '40px',
    position: 'absolute',
    bottom: '30px',
    right: '45px',
  },
  total: {
    margin: theme.spacing(-1, -2, 1, -2),
    display: 'flex',
    flexBasis: 'calc(100% + 32px)',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: theme.palette.action.hover,
    position: 'relative',

    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(-3, -3, 1, -3),
    },
  },
  noTargetSystems: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 10,
    right: 10,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',

    [theme.breakpoints.up('lg')]: {
      top: '64px',
      bottom: 0,
      right: 0,
      left: '230px',
    },
  },
  noTargetSystemIcon: {
    fontSize: '6rem',
    color: chartColor(categories.CRITICAL.value),
  },
}));

const TargetSystems = memo(() => {
  const classes = useStyles();
  const config = useConfig();
  const location = useLocation();
  const filters = wrapFilters(filtersFromQueryString(location.search));
  let showWarning = false;
  const [rmDevicesConfirm, setRmDevicesConfirm] = useState<string | null>(null);
  const [rmTSConfirm, setRmTSConfirm] = useState<MedisanteTargetSystem | null>(
    null
  );
  const [disableTSConfirm, setDisableTSConfirm] =
    useState<MedisanteTargetSystem | null>(null);
  const { state: globalState, dispatch } = useManageContext();
  const [blockLoading, setBlockLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { authUser } = useAuthUser();

  const orgDefaultWebhooks: Record<string, boolean> = Object.fromEntries(
    authUser.orgs.map((o) => [o.id, !!o.useDefaultWebhooks])
  );

  const {
    isLoading: isLoadingTs,
    isFetching: isFetchingTs,
    error,
    data: initialTargetSystems = [],
    refetch: fetchTargetSystems,
  } = useGetTargetSystemsQuery({
    apiBaseURL: config.apiBaseURL,
    orgs: authUser.selectedOrgs,
    filters: filtersFromQueryString(location.search).filter(
      (f) => f.category !== 'configurationWarning'
    ),
  });

  const isLoadingTargetSystems = isLoadingTs || isFetchingTs;
  const { isLoading: isFetchingDevices, data: devices = [] } =
    useGetDevicesQuery(
      {
        apiBaseURL: config.apiBaseURL,
        filters: [
          {
            category: 'includeStock',
            value: 'true',
          },
          {
            category: 'includeBlocked',
            value: 'true',
          },
        ],
        orgs: authUser.selectedOrgs,
      },
      { skip: !!error }
    );
  const [removeDevicesFromTs, { isLoading: isRemovingDevices }] =
    useRemoveTsDevicesMutation();
  const [removeTs, { isLoading: isRemovingTs }] = useRemoveTsMutation();
  const [updateTs, { isLoading: isUpdatingTs }] = useEditTargetSystemMutation();

  const isLoadingDevices =
    isFetchingDevices || isRemovingDevices || isRemovingTs || isUpdatingTs;

  const [isOpen, setOpen] = useState(false);

  const deviceInfo = useCallback(
    (targetSystemId: string) =>
      partialRight(
        deviceForTargetSystemInfo,
        devices,
        authUser.orgs
      )(targetSystemId),
    [devices, authUser.orgs]
  );

  const incompatibleTs = initialTargetSystems
    .filter((ts) => {
      const r = deviceInfo(ts.id);
      return (
        r.count > 0 && r.incompatibile && ts.format && ts.format.match(/fhir/i)
      );
    })
    .map((ts) => ts.id);

  let targetSystems: MedisanteTargetSystem[] = useMemo(() => {
    if (!isLoadingTargetSystems) {
      let tss = initialTargetSystems;
      // UI filtering only
      if (filters.filter((f) => f.category === 'configurationWarning').length) {
        tss = tss.filter((ts) => incompatibleTs.indexOf(ts.id) !== -1);
      }

      return sortTargetSystems(
        tss,
        globalState.sortStates.targetSystemsSortState,
        deviceInfo
      );
    }
    return [];
  }, [
    deviceInfo,
    filters,
    globalState.sortStates.targetSystemsSortState,
    incompatibleTs,
    initialTargetSystems,
    isLoadingTargetSystems,
  ]);

  const [checkRunning, setCheckRunning] = useState(0);

  const handleSortStateChange = React.useCallback(
    (sortState) => {
      dispatch({
        type: 'change-target-systems-sort-state',
        data: sortState,
      });
    },
    [dispatch]
  );

  const { sortState, updateSortState, currentOptionId } = useSortState<
    SortState<MedisanteTargetSystem>,
    TargetSystemsSortBy
  >(
    targetSystemsSortingReducer,
    globalState.sortStates.targetSystemsSortState,
    handleSortStateChange,
    mobileSortOptions
  );

  const [selectedSystem, setSelectedSystem] =
    useState<MedisanteTargetSystem | null>(null);
  const [selectedTestSystem, setSelectedTestSystem] =
    useState<MedisanteTargetSystem | null>(null);

  const handleAddTargetSystem = () => setOpen(true);

  const setSnack = useCallback(
    (snack: string) => {
      if (!!snack) {
        enqueueSnackbar(snack, { variant: 'info' });
      }
    },
    [enqueueSnackbar]
  );

  const handleCheckStatus = useCallback(async () => {
    const timeout = (delay: number) => {
      return new Promise((res) => setTimeout(res, delay));
    };
    setBlockLoading(true);
    await timeout(4000);
    setBlockLoading(false);
    fetchTargetSystems();
  }, [fetchTargetSystems]);

  useEffect(() => {
    if (
      checkRunning &&
      selectedTestSystem !== null &&
      !isLoadingTargetSystems &&
      !blockLoading
    ) {
      if (checkRunning > 15) {
        setCheckRunning(0);
        setSnack(`Test did not complete in a normal period of time.`);
        setSelectedTestSystem(null);
        return;
      }
      const ts = targetSystems.find((t) => t.id === selectedTestSystem.id);
      if (ts && ts.lastStatusUpdate !== selectedTestSystem.lastStatusUpdate) {
        setSnack(`Test end with status: ${ts.lastStatusCode}`);
        setSelectedTestSystem(null);
        setCheckRunning(0);
        return;
      }
      setCheckRunning(checkRunning + 1);
      handleCheckStatus();
    }
  }, [
    checkRunning,
    handleCheckStatus,
    selectedTestSystem,
    targetSystems,
    setSnack,
    isLoadingTargetSystems,
    blockLoading,
  ]);

  const handleClose = async () => {
    setCheckRunning(1);
  };

  const contentVisible =
    targetSystems.length > 0 ||
    initialTargetSystems.length > 0 ||
    filters.length > 0;

  if (selectedSystem != null) {
    const unsupportedDevices = devices.filter(
      (d) =>
        notFullySuportedModel.indexOf(d.model) !== -1 &&
        d.owner === selectedSystem.owner
    );
    showWarning = unsupportedDevices.length > 0;
  }

  const refresh = (
    <div className={classes.refresh}>
      <Visible when={isLoadingTargetSystems}>
        <span className={classes.refreshProgressWrapper}>
          <CircularProgress className={classes.refreshProgress} />
        </span>
      </Visible>
      <span className={classes.refreshIcon}>
        <IconButton
          onClick={() => fetchTargetSystems()}
          disabled={isLoadingTargetSystems}
          title="Refresh webhooks"
        >
          <Cached />
        </IconButton>
      </span>
    </div>
  );

  const updateTargetSystem = (ts: MedisanteTargetSystem) => {
    return updateTs({
      apiBaseURL: config.apiBaseURL,
      targetSystem: ts,
    })
      .then((res: any) => {
        if (res.error) {
          enqueueSnackbar(res.error.message, { variant: 'error' });
        }
      })
      .catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      });
  };

  return (
    <div className={classes.page}>
      <Layout
        isLoading={
          isLoadingDevices ||
          (isLoadingTargetSystems &&
            targetSystems.length === 0 &&
            checkRunning === 0)
        }
        error={error}
      >
        <Visible when={contentVisible}>
          <MediaDown query="sm">
            <Grid item className={classes.total}>
              <Typography variant="subtitle1" color="textSecondary">
                Total webhooks count: {targetSystems.length}
              </Typography>
            </Grid>
            <Filters filters={filters} />
            <Grid container>
              <Grid item xs={8} sm={10}>
                <MedSelect
                  name="sort-option"
                  placeholder="Sort by"
                  variant="outlined"
                  fullWidth
                  options={mobileSortOptions}
                  valueGetter={(o: SortOptionSelectItem<TargetSystemsSortBy>) =>
                    o.id
                  }
                  optionTextGetter={(
                    o: SortOptionSelectItem<TargetSystemsSortBy>
                  ) => o.label}
                  value={currentOptionId}
                  onChange={(e) => {
                    const option = mobileSortOptions.find(
                      (o) => o.id === e.target.value
                    );
                    option && updateSortState(option.value);
                  }}
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                {refresh}
              </Grid>
            </Grid>
            <MobileLayout
              onTest={(item) => setSelectedTestSystem(item)}
              onEdit={(item) => setSelectedSystem(item)}
              deviceInfo={deviceInfo}
              targetSystems={targetSystems}
              removeDevices={setRmDevicesConfirm}
              removeTargetSystem={setRmTSConfirm}
              updateTargetSystem={updateTargetSystem}
              disableTargetSystem={setDisableTSConfirm}
              orgDefaultWebhooks={orgDefaultWebhooks}
            />
          </MediaDown>
          <MediaUp query="md">
            <Filters filters={filters} />
            <DesktopLayout
              targetSystems={targetSystems}
              sortState={sortState}
              updateSortState={updateSortState}
              selectedSystem={selectedSystem}
              setSelectedSystem={setSelectedSystem}
              selectedTestSystem={selectedTestSystem}
              setSelectedTestSystem={setSelectedTestSystem}
              deviceInfo={deviceInfo}
              removeDevices={setRmDevicesConfirm}
              isLoadingTargetSystems={isLoadingTargetSystems}
              fetchTargetSystems={fetchTargetSystems}
              removeTargetSystem={setRmTSConfirm}
              updateTargetSystem={updateTargetSystem}
              disableTargetSystem={setDisableTSConfirm}
              orgDefaultWebhooks={orgDefaultWebhooks}
            />
          </MediaUp>
        </Visible>
        <Visible when={!contentVisible}>
          <div className={classes.noTargetSystems}>
            <DeviceHub className={classes.noTargetSystemIcon} />
            No webhook found
          </div>
        </Visible>

        <Fab
          title="Add webhook"
          className={classes.fab}
          disabled={
            !hasOneOfOrgRoles(authUser, authUser.selectedOrgs, [
              Role.DeviceManager,
            ])
          }
          onClick={handleAddTargetSystem}
          color="primary"
        >
          <AddIcon />
        </Fab>

        <ConfirmationDialog
          isOpen={rmDevicesConfirm != null}
          confirmationText="Confirm"
          cancelText="Cancel"
          questionText="Are you sure that you want remove all devices from webhook?"
          title="Remove devices from webhook"
          onClose={() => {
            setRmDevicesConfirm(null);
          }}
          onConfirm={() => {
            if (rmDevicesConfirm != null) {
              removeDevicesFromTs({
                id: rmDevicesConfirm,
                apiBaseURL: config.apiBaseURL,
              }).then(() => {
                setSnack('Devices removed');
              });
              setRmDevicesConfirm(null);
            }
          }}
        />

        <ConfirmationDialog
          isOpen={rmTSConfirm != null}
          confirmationText="Confirm"
          cancelText="Cancel"
          questionText="Are you sure that you want remove this webhook?"
          title="Remove webhook"
          onClose={() => {
            setRmTSConfirm(null);
          }}
          onConfirm={() => {
            if (rmTSConfirm != null) {
              removeTs({ id: rmTSConfirm.id, apiBaseURL: config.apiBaseURL })
                .then(() => {
                  setRmTSConfirm(null);
                  setSnack('Webhook removed');
                })
                .catch((e) => {
                  setSnack(e.message);
                });
            }
          }}
        />
        <ConfirmationAction
          isOpen={disableTSConfirm != null}
          confirmationText="Confirm"
          cancelText="Cancel"
          title={
            disableTSConfirm?.deactivated ? 'Unblock webhook' : 'Block webhook'
          }
          onClose={() => {
            setDisableTSConfirm(null);
          }}
          onConfirm={() => {
            if (disableTSConfirm != null) {
              updateTargetSystem({
                deactivated: !disableTSConfirm.deactivated,
                id: disableTSConfirm.id,
                name: disableTSConfirm.name,
                url: disableTSConfirm.url,
                isDefault: disableTSConfirm.isDefault,
              })
                .then(() => {
                  setDisableTSConfirm(null);
                  setSnack(
                    disableTSConfirm?.deactivated
                      ? 'Webhook unblocked'
                      : 'Webhook blocked'
                  );
                })
                .catch((e) => {
                  setSnack(e.message);
                });
            }
          }}
        >
          {disableTSConfirm?.deactivated
            ? 'Are you sure that you want unblock this webhook?'
            : 'Are you sure that you want block this webhook?'}

          <Alert severity="warning" style={{ marginTop: '1em' }}>
            Active webhook required for outbound measurement routing
          </Alert>
        </ConfirmationAction>
        <EditTargetSystemDialog
          open={isOpen}
          showWarning={true}
          onChange={(warnings?: string) => {
            setSnack(
              warnings ? `Webhook created, but ${warnings}` : 'Webhook created'
            );
          }}
          onClose={() => {
            setOpen(false);
          }}
          title="Add Webhook"
          targetSystems={targetSystems}
        />
        <EditTargetSystemDialog
          open={selectedSystem != null}
          showWarning={showWarning}
          onChange={(warnings?: string) => {
            setSnack(
              warnings ? `Webhook changed, but ${warnings}` : 'Webhook changed'
            );
          }}
          onClose={() => {
            setSelectedSystem(null);
          }}
          title="Edit webhook"
          data={selectedSystem}
          targetSystems={targetSystems}
        />
      </Layout>
      {selectedTestSystem !== null && (
        <TriggerTestDialog
          targetSystem={selectedTestSystem}
          onClose={handleClose}
          isOpen={true}
          isCare={!selectedTestSystem.isMutable}
          setSnack={setSnack}
          allowModels={
            authUser.orgs.find((e) => e.id === selectedTestSystem.owner)
              ?.allowTestModels || []
          }
          onForceClose={() => {
            setSelectedTestSystem(null);
          }}
        />
      )}
    </div>
  );
});

export default TargetSystems;
